import {
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputBase,
  MenuItem,
  Accordion as MuiAccordion,
  Select,
  Switch,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { Editor as TipTapEditor, useEditor } from "@tiptap/react";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SetStateAction, useContext, useEffect, useState } from "react";
import { GmailThread, OutreachContact } from "schemas/dashboard";
import { EmailAddress, EmailDraft, EmailThreadSetting } from "schemas/email";
import { CustomEvent } from "schemas/functions";

import Editor from "components/Editor";
import { extensions } from "components/Editor/utils";
import AttachmentManager from "features/Influencer/ContactList/AttachmentManager";
import { generateColorFromString } from "utils/color";
import { sanitize } from "utils/sanitize";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import RecipientAutocomplete from "./RecipientAutocomplete";
import SendButtonGroup from "./SendButtonGroup";
import styles from "./styles";
import { generateDefaultTime } from "./utils";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

interface EmailDraftComponentProps {
  from: EmailAddress;
  gmailThread: GmailThread;
  emailDraft: EmailDraft;
  setEmailDraft: React.Dispatch<SetStateAction<EmailDraft>>;
  handleSendDraftEmail: (sendAt?: null | string) => void;
  cancelDraft: () => void;
  sendLoading: boolean;
  emailThreadSetting: EmailThreadSetting;
  setEmailThreadSetting: React.Dispatch<SetStateAction<EmailThreadSetting>>;
  displaySubjectEditor?: boolean;
  currentContact: OutreachContact;
  displayFollowUp?: boolean;
  followUpDesign?: boolean;
}

export default function EmailDraftComponent({
  emailDraft,
  from,
  gmailThread,
  setEmailDraft,
  handleSendDraftEmail,
  cancelDraft,
  sendLoading,
  emailThreadSetting,
  setEmailThreadSetting,
  displaySubjectEditor,
  currentContact,
  displayFollowUp,
  followUpDesign,
}: EmailDraftComponentProps) {
  const {
    getLatestFollowUpEmail,
    getDefaultFollowUpTemplate,
    personalizeFollowUpBody,
    followupTemplates,
    currentDraftBody,
    setCurrentDraftBody,
  } = useContext(OutreachTemplatesContext);
  const [attachmentDialogOpen, setAttachmentDialogOpen] =
    useState<boolean>(false);
  const [updateEditor, setUpdateEditor] = useState<boolean>(false);
  const body = sanitize(emailDraft?.body || "");

  const handleGetBody = async () => {
    if (emailDraft?.body && emailDraft?.body?.length > 0) return;
    const template = getDefaultFollowUpTemplate();
    const followUpBody = await getLatestFollowUpEmail(currentContact);
    setEmailDraft((prev) => {
      return { ...prev, body: followUpBody, templateId: template?.id };
    });
    setUpdateEditor(true);
  };

  const editor = useEditor(
    {
      extensions: extensions(),
      content: body,
      onUpdate({ editor }) {
        setEmailDraft((prev) => {
          return { ...prev, isChanged: true, body: editor.getHTML() };
        });
      },
    },
    [updateEditor],
  ) as TipTapEditor;

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    trackEvent("Disable Follow Up Tasks Checkbox Checked", { emailDraft });
    setEmailThreadSetting({
      ...emailThreadSetting,
      disableFollowUpTasks: event.target.checked,
    });
  };

  const handleSubjectChange = (e: CustomEvent) => {
    trackEvent(`Inbox Subject Changed`, {
      "Gmail Thread ID": gmailThread?.id,
      emailDraft,
    });
    setEmailDraft((prev) => {
      return { ...prev, subject: e.target.value };
    });
  };

  const handleSelectFollowUpTemplate = (e: CustomEvent) => {
    const value = e.target.value;
    if (!value) {
      setEmailDraft((prev) => ({ ...prev, templateId: undefined }));
      return;
    }
    const templateId = Number(value);
    const template = followupTemplates.find((t) => t.id === templateId);
    if (!template) return;

    trackEvent("Template Changed", {
      "Template Name": template.name,
      Source: "Inbox Reply",
    });

    const personalizedBody = personalizeFollowUpBody(
      template.body || "",
      currentContact,
    );
    setCurrentDraftBody(personalizedBody);
    setEmailDraft((prev) => ({
      ...prev,
      isChanged: true,
      body: personalizedBody,
      attachment: template.attachment,
      templateId,
    }));
  };

  const handleCcContactsChange = (newContacts: EmailAddress[]) => {
    trackEvent(`Outreach Contact Thread ccContacts Input Changed`, {
      "Gmail Thread ID": gmailThread?.id,
      emailDraft,
    });
    setEmailDraft((prev) => {
      return { ...prev, ccContacts: newContacts };
    });
  };

  const handleToContactsChange = (newContacts: EmailAddress[]) => {
    trackEvent(`Outreach Contact Thread toContacts Input Changed`, {
      "Gmail Thread ID": gmailThread?.id,
      emailDraft,
    });
    setEmailDraft((prev) => {
      return { ...prev, toContacts: newContacts };
    });
  };

  const allParticipants = [
    ...gmailThread.messages[gmailThread.messages.length - 1]?.cc,
    gmailThread.messages[gmailThread.messages.length - 1]?.from_,
    ...gmailThread.messages[gmailThread.messages.length - 1]?.bcc,
    ...gmailThread.messages[gmailThread.messages.length - 1]?.to,
  ];

  useEffect(() => {
    if (displayFollowUp) {
      handleGetBody();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayFollowUp]);

  useEffect(() => {
    if (
      editor &&
      currentDraftBody?.length > 0 &&
      editor.getHTML() !== currentDraftBody
    ) {
      editor.commands.setContent(makeDeepCopy(currentDraftBody), true);
      setCurrentDraftBody("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDraftBody, editor?.getHTML()]);

  return (
    <Accordion
      disableGutters
      square
      expanded={true}
      elevation={0}
      sx={{
        width: "100%",
        borderTop: "none",
        borderBottom: "none",
      }}
    >
      <AccordionSummary
        sx={
          followUpDesign
            ? [
                styles.summary,
                { px: 0, "& .MuiAccordionSummary-content": { my: 0 } },
              ]
            : styles.summary
        }
      >
        <Grid container item xs={12}>
          {!followUpDesign && from.email && (
            <Grid item xs="auto">
              <Avatar
                sx={{
                  background: generateColorFromString(from.email),
                  textTransform: "uppercase",
                  mr: 1,
                }}
              >
                {from.name?.charAt(0) || from.email.charAt(0)}
              </Avatar>
            </Grid>
          )}
          <Grid
            item
            container
            xs
            sx={
              followUpDesign
                ? [styles.summaryText, { width: "100%" }]
                : styles.summaryText
            }
          >
            {!followUpDesign && (
              <Grid item xs={12}>
                {(from?.name || from?.email) && (
                  <Typography>
                    {from.name || from.email}{" "}
                    <Typography
                      component="span"
                      color="textSecondary"
                      variant="caption"
                      sx={{ ml: 1 }}
                    >
                      now
                    </Typography>
                  </Typography>
                )}
              </Grid>
            )}
            <Grid
              item
              xs={12}
              container
              sx={followUpDesign ? styles.followUpRow : { py: 0.5 }}
              spacing={followUpDesign ? 0 : 1}
              alignItems="center"
            >
              <Grid item xs="auto">
                <Typography
                  component="div"
                  sx={followUpDesign ? styles.followUpLabel : { width: 25 }}
                  variant="caption"
                  color="textSecondary"
                >
                  {followUpDesign ? "To" : "to:"}
                </Typography>
              </Grid>
              <Grid item xs sx={{ minWidth: 0 }}>
                <RecipientAutocomplete
                  options={allParticipants}
                  value={emailDraft.toContacts}
                  updateValue={handleToContactsChange}
                  variant={followUpDesign ? "noborder" : "outlined"}
                  placeholder={followUpDesign ? "Pick a contact" : undefined}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sx={followUpDesign ? styles.followUpRow : { py: 0.5 }}
              container
              spacing={followUpDesign ? 0 : 1}
              alignItems="center"
            >
              <Grid item xs="auto">
                <Typography
                  component="div"
                  sx={followUpDesign ? styles.followUpLabel : { width: 25 }}
                  variant="caption"
                  color="textSecondary"
                >
                  {followUpDesign ? "Cc" : "cc:"}
                </Typography>
              </Grid>
              <Grid item xs sx={{ minWidth: 0 }}>
                <RecipientAutocomplete
                  options={allParticipants}
                  value={emailDraft.ccContacts}
                  updateValue={handleCcContactsChange}
                  variant={followUpDesign ? "noborder" : "outlined"}
                  placeholder={followUpDesign ? "Pick a contact" : undefined}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails sx={followUpDesign ? { pt: 0, px: 0 } : { pt: 0 }}>
        {followupTemplates?.length > 0 && (
          <Box
            sx={
              followUpDesign
                ? styles.followUpSubjectRow
                : [styles.subjectWrapper, { pt: 0 }]
            }
          >
            <Typography
              component="span"
              sx={
                followUpDesign
                  ? [styles.followUpLabel, { width: "auto", mr: 1 }]
                  : styles.annotation
              }
            >
              {followUpDesign ? "Follow-Up Template" : "Follow-Up Template:"}
            </Typography>
            <Select
              onChange={handleSelectFollowUpTemplate}
              value={emailDraft.templateId ?? ""}
              displayEmpty
              size="small"
              sx={[
                styles.templateSelect,
                styles.noSelectBorder,
                styles.templateWidth,
              ]}
            >
              <MenuItem value="">
                <em>No Template</em>
              </MenuItem>
              {followupTemplates.map((template) => (
                <MenuItem
                  key={template.id}
                  value={template.id}
                  sx={styles.templateMenuItem}
                >
                  <Box sx={styles.templateMenuName}>
                    {template.name || template.subject}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
        {displaySubjectEditor &&
          (followUpDesign ? (
            <Box sx={styles.followUpSubjectRow}>
              <Typography component="span" sx={styles.followUpLabel}>
                Subject
              </Typography>
              <InputBase
                fullWidth
                placeholder="Subject"
                value={emailDraft?.subject}
                onChange={handleSubjectChange}
                sx={styles.followUpSubjectInput}
              />
            </Box>
          ) : (
            <TextField
              label="Subject"
              fullWidth
              size="small"
              placeholder="Type your email subject here..."
              sx={{ my: 2 }}
              value={emailDraft?.subject}
              onChange={handleSubjectChange}
            />
          ))}
        <Editor
          editor={editor}
          allowAttachment={true}
          openAttachmentManager={() => {
            setAttachmentDialogOpen(true);
          }}
          attachment={emailDraft.attachment}
          removeAttachment={() =>
            setEmailDraft((prev) => {
              return { ...prev, attachment: undefined };
            })
          }
          allowImage={true}
          showVariableError
          bottomToolbar={followUpDesign}
          hasBorder={!followUpDesign}
        />
        <Grid container sx={{ mt: 1, ml: 1 }} alignItems="center">
          <Grid item xs={12}>
            {followUpDesign ? (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    color="primary"
                    onChange={handleCheckboxChange}
                    checked={emailThreadSetting.disableFollowUpTasks}
                  />
                }
                sx={styles.followUpToggle}
                label="Don't remind me about this thread"
              />
            ) : (
              <FormControlLabel
                control={
                  <Checkbox
                    edge="start"
                    onChange={handleCheckboxChange}
                    checked={emailThreadSetting.disableFollowUpTasks}
                    disableRipple
                    icon={
                      <Box
                        component="i"
                        className="fa-regular fa-square"
                        sx={{ color: "green.main" }}
                      />
                    }
                    checkedIcon={
                      <Box
                        component="i"
                        className="fa-regular fa-square-check"
                        sx={{ color: "green.main" }}
                      />
                    }
                  />
                }
                sx={styles.checkbox}
                label="Don't Remind Me About This Thread"
              />
            )}
          </Grid>
          {!followUpDesign && (
            <Grid container justifyContent="flex-end" item xs={12}>
              <Button
                color="secondary"
                size="small"
                sx={{ mr: 1, my: 1 }}
                disableElevation
                variant="outlined"
                onClick={cancelDraft}
              >
                Cancel
              </Button>
              <SendButtonGroup
                handleSendDraftEmail={handleSendDraftEmail}
                sendLoading={sendLoading}
                defaultTime={generateDefaultTime(gmailThread)}
                disabled={
                  emailDraft.body?.length < 10 ||
                  (displaySubjectEditor &&
                    emailDraft.subject?.trim()?.length < 5)
                }
                buttonTheme={{
                  mt: 1,
                }}
                arrowTheme={{
                  mt: 1,
                }}
              />
            </Grid>
          )}
        </Grid>
        <AttachmentManager
          enteredSelection={(attachment: string) =>
            setEmailDraft((prev) => {
              return { ...prev, attachment };
            })
          }
          open={attachmentDialogOpen}
          setOpen={setAttachmentDialogOpen}
        />
      </AccordionDetails>
    </Accordion>
  );
}
